import { Alert, AlertTitle, Avatar, Box, Button, Chip, Grid, Typography, styled } from "@mui/material";
import { Outlet } from "react-router-dom";
import AlertContext from "../AlertContext";
import logo from "../assets/prozo.png";

const TEAL = '#4ECDC4';

const LeftPanel = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(165deg, #0B1B2E 0%, #0D2644 55%, #0B1B2E 100%)',
  height: '100%',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(5, 4),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-80px',
    right: '-80px',
    width: '380px',
    height: '380px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(78,205,196,0.1) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-100px',
    left: '-60px',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(238,45,65,0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
}));

const StatCard = styled(Box)(({ theme }) => ({
  background: 'rgba(255,255,255,0.05)',
  borderRadius: 12,
  padding: theme.spacing(1.75, 1.75),
  border: '1px solid rgba(255,255,255,0.08)',
  transition: 'background 0.25s, border-color 0.25s, transform 0.25s, box-shadow 0.25s',
  cursor: 'default',
  '&:hover': {
    background: 'rgba(255,255,255,0.1)',
    borderColor: 'rgba(78,205,196,0.3)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  background: '#FFFFFF',
  height: '100%',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4, 3),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(6, 5),
  },
}));

const FormWrapper = styled(Box)(() => ({
  width: '100%',
  maxWidth: 520,
}));

const stats = [
  { value: '52+', label: 'Fulfilment Centers' },
  { value: '3M+', label: 'Sq. Ft. Warehouse Space' },
  { value: '24,000+', label: 'Pin Codes Served' },
  { value: '50+', label: 'Marketplace Integrations' },
];

const tags = ['Pay-Per-Use', 'SLA-Compliant', 'Multi-Channel', 'Tech-Enabled', 'Pan-India'];

function AuthLayout() {
  return (
    <Grid container component="main" sx={{ height: '100vh', overflow: 'hidden' }}>
      <Grid item xs={false} md={5} sx={{ display: { xs: 'none', md: 'flex' }, height: '100%' }}>
        <LeftPanel>
          <Avatar
            src={logo}
            sx={{
              height: 36,
              width: 100,
              borderRadius: 0,
              mb: 4,
              '& img': {
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)',
              },
            }}
          />

          <Typography sx={{ color: '#FFFFFF', mb: 1, lineHeight: 1.35, maxWidth: 320, fontSize: 22, fontWeight: 700 }}>
            Your E2E{' '}
            <Box component="span" sx={{ color: TEAL }}>
              Supply Chain
            </Box>{' '}
            Partner
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.55)', mb: 4, maxWidth: 320, lineHeight: 1.8, fontSize: 12 }}
          >
            Enterprise-grade, SLA-compliant warehousing &amp; fulfilment for B2B, B2C, D2C and
            Omnichannel brands.
          </Typography>

          <Grid container spacing={1.5} sx={{ mb: 4 }}>
            {stats.map((stat) => (
              <Grid item xs={6} key={stat.label}>
                <StatCard>
                  <Typography sx={{ color: TEAL, fontWeight: 700, mb: 0.5, fontSize: 18 }}>
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1.5, display: 'block', fontSize: 11 }}
                  >
                    {stat.label}
                  </Typography>
                </StatCard>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
                sx={{
                  color: 'rgba(255,255,255,0.65)',
                  borderColor: 'rgba(255,255,255,0.18)',
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  fontSize: 10,
                  height: 24,
                }}
              />
            ))}
          </Box>
        </LeftPanel>
      </Grid>

      <Grid item xs={12} md={7} sx={{ height: '100%', display: 'flex' }}>
        <RightPanel sx={{ flex: 1 }}>
          <FormWrapper>
            <AlertContext.Consumer>
              {({ message, setMessage }) =>
                message && (
                  <Alert
                    action={
                      <Button color="inherit" size="small" onClick={() => setMessage(null)}>
                        Close
                      </Button>
                    }
                    severity="error"
                    sx={{
                      width: 1,
                      mb: 3,
                      borderRadius: 2,
                      background: 'rgba(238,45,65,0.06)',
                      border: '1px solid rgba(238,45,65,0.22)',
                      color: '#C41E30',
                      '& .MuiAlert-icon': { color: '#EE2D41' },
                    }}
                  >
                    <AlertTitle sx={{ fontWeight: 700, fontSize: 13 }}>Login Error</AlertTitle>
                    {message}
                  </Alert>
                )
              }
            </AlertContext.Consumer>
            <Outlet />
          </FormWrapper>
        </RightPanel>
      </Grid>
    </Grid>
  );
}

export default AuthLayout;
